import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {ListChildComponentProps, VariableSizeList} from 'react-window';

import {CodonTranslationTable, NucleotideType} from 'biojs/BioConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {EditTranslationRegionGroupData} from 'molbio/dao/SequenceDAO';
import {
  getTranslationNameOrPlaceholder,
  TranslationRegionGroupData,
} from 'molbio/translations/TranslationUtil';
import {BenchlingDispatch} from 'redux-stores/Store';
import getTestProps from 'test/lib/cypress/getTestProps';
import {
  cancelCreateNewTranslation,
  openCreateNewTranslation,
} from 'toolbar/feature-panel/FeaturePanelUIReduxActions';
import FeatureRowItem, {BASE_FEATURE_ROW_HEIGHT} from 'toolbar/feature-panel/FeatureRowItem';
import {useFeaturePanelStateBySequence} from 'toolbar/feature-panel/reduceFeaturePanelUI';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';

import CreateEditTranslationForm from './CreateEditTranslationForm';
import {CreateNewTranslationData, translationPositionToString} from './TranslationPanelUtil';

export interface TranslationPanelContentProps {
  sortedTranslationRegionGroups: Array<TranslationRegionGroupData>;
  canEditSequenceFeatures: boolean;
  sequenceId: string;
  sequenceStartIndex: number;
  sequenceLength: number;
  sequenceBases: string;
  nucleotideType: NucleotideType;
  isSingleStranded: boolean;
  isCircularSequence: boolean;
  onSelectTranslation: (groupCid: string, selected: boolean) => void;
  deleteTranslationRegionGroup: (groupCid: string) => Promise<void>;
  editTranslationRegionGroup: (groupCid: string, data: EditTranslationRegionGroupData) => void;
  createNewTranslationRegionGroup: (data: CreateNewTranslationData) => void;
  openExpandedTranslations: () => void;
  tagSchema?: TagSchema;
  className?: string;
}

interface TranslationPanelRowVirtualizedListData {
  sortedTranslationRegionGroups: Array<TranslationRegionGroupData>;
  canEditSequenceFeatures: boolean;
  sequenceId: string;
  sequenceStartIndex: number;
  sequenceLength: number;
  sequenceBases: string;
  nucleotideType: NucleotideType;
  isSingleStranded: boolean;
  isCircularSequence: boolean;
  onSelectTranslation: (groupCid: string, selected: boolean) => void;
  deleteTranslationRegionGroup: (groupCid: string) => Promise<void>;
  editTranslationRegionGroup: (groupCid: string, data: EditTranslationRegionGroupData) => void;
  createNewTranslationRegionGroup: (data: CreateNewTranslationData) => void;
  openExpandedTranslations: () => void;
  tagSchema?: TagSchema;
}

// Heights of various elements in each row used to calculate the row height for virtualization
const EDIT_FORM_BASE_FIELD_HEIGHT = 43;
const NUM_EDIT_FIELDS = 6;
const EMPTY_NOTES_FIELD_HEIGHT = 50;
const NOTES_FIELD_HEIGHT = 80;
const CUSTOM_FIELD_BASE_HEIGHT = 56;
const CUSTOM_FIELD_ROW_HEIGHT = 44;
const REGION_BASE_HEIGHT = 38;
const REGION_ROW_HEIGHT = 43;
const EDIT_FORM_BUTTONS_HEIGHT = 45;
// Shorter than other edit fields so special-case.
const TRANSLATE_AS_METHIONINE_FIELD_HEIGHT = 31;

/**
 * Renders a single translation row based on the props provided by the virtualized list.
 *
 * Note: This function needs to be defined at the top level (instead of inline in the panel component as
 * a closure). Otherwise, the edit form will lose focus on each update.
 * See: https://github.com/bvaughn/react-window/issues/420
 */
function TranslationPanelRow({
  index,
  data,
  style,
}: ListChildComponentProps<TranslationPanelRowVirtualizedListData>): JSX.Element {
  const translationRegionGroup = data.sortedTranslationRegionGroups[index];
  // Update the computed style to cap the height and add a scrollbar.
  const updatedStyle = {
    ...style,
    maxHeight: style.height,
    overflow: 'auto',
  };
  const contextMenuOptions = [
    {
      name: 'Remove translation',
      click: () => {
        data.deleteTranslationRegionGroup(translationRegionGroup.cid);
      },
      hide: !data.canEditSequenceFeatures || !translationRegionGroup.editable,
    },
    {
      name: 'Expand translation',
      click: data.openExpandedTranslations,
    },
  ];

  return (
    <div style={updatedStyle}>
      <FeatureRowItem
        key={translationRegionGroup.cid}
        feature={translationRegionGroup}
        getFeatureName={(group) =>
          getTranslationNameOrPlaceholder(group, data.sequenceStartIndex, data.sequenceLength)
        }
        getLocationString={(group) =>
          translationPositionToString(group, data.sequenceStartIndex, data.sequenceLength)
        }
        editForm={
          <CreateEditTranslationForm
            allTranslationData={data.sortedTranslationRegionGroups}
            editedTranslationData={translationRegionGroup}
            sequenceId={data.sequenceId}
            sequenceStartIndex={data.sequenceStartIndex}
            sequenceLength={data.sequenceLength}
            sequenceBases={data.sequenceBases}
            nucleotideType={data.nucleotideType}
            isSingleStranded={data.isSingleStranded}
            isCircularSequence={data.isCircularSequence}
            onClickCancel={() => {
              data.onSelectTranslation(translationRegionGroup.cid, false);
            }}
            editTranslationRegionGroup={data.editTranslationRegionGroup}
            createNewTranslationRegionGroup={data.createNewTranslationRegionGroup}
            deleteTranslationRegionGroup={data.deleteTranslationRegionGroup}
            isReadOnly={!data.canEditSequenceFeatures || !translationRegionGroup.editable}
            tagSchema={data.tagSchema}
          />
        }
        contextMenuOptions={contextMenuOptions}
        onSelect={data.onSelectTranslation}
      />
    </div>
  );
}

/**
 * The contents of the translations tab in the features panel in the molbio toolbar for DNA and RNA.
 * Handles creating and editing translations and translation regions.
 */
export default function TranslationPanelContent({
  sortedTranslationRegionGroups,
  canEditSequenceFeatures,
  sequenceId,
  sequenceStartIndex,
  sequenceLength,
  sequenceBases,
  nucleotideType,
  isSingleStranded,
  isCircularSequence,
  onSelectTranslation,
  deleteTranslationRegionGroup,
  editTranslationRegionGroup,
  createNewTranslationRegionGroup,
  openExpandedTranslations,
  tagSchema,
  className,
}: TranslationPanelContentProps): JSX.Element {
  const {translationPanelState} = useFeaturePanelStateBySequence(sequenceId);
  const dispatch = useDispatch<BenchlingDispatch>();
  const selectedTranslationIndex = sortedTranslationRegionGroups.findIndex((g) => g.selected);

  const listRef = useRef<VariableSizeList>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // react-window caches the row sizes. Whenever the selection changes we need to bust the cache for all
    // rows because the currently selected row and the previously selected row both changed size.
    listRef.current?.resetAfterIndex(0);
    if (selectedTranslationIndex !== -1) {
      listRef.current?.scrollToItem(selectedTranslationIndex, 'start');
    }
  }, [selectedTranslationIndex]);

  useEffect(() => {
    // When opening the create form, scroll to the bottom so that the save button is visible
    if (panelRef.current != null && translationPanelState.isCreateActive) {
      panelRef.current.scrollTop = panelRef.current.scrollHeight;
    }
  }, [translationPanelState.isCreateActive]);

  function onClickCreate(): void {
    if (selectedTranslationIndex !== -1) {
      onSelectTranslation(sortedTranslationRegionGroups[selectedTranslationIndex].cid, false);
    }
    dispatch(openCreateNewTranslation(sequenceId));
  }

  function getTranslationPanelRowHeight(index: number): number {
    if (!sortedTranslationRegionGroups[index]?.selected) {
      return BASE_FEATURE_ROW_HEIGHT;
    }

    // Calculate an approximate size for the expanded row based on the size of the elements in the edit
    // form. This value is used to set the maxHeight for the expanded row. The main goal is to set the height
    // large enough to avoid a nested scrollbar when first viewing an expanded translation.
    return (
      // extra padding
      BASE_FEATURE_ROW_HEIGHT +
      EDIT_FORM_BASE_FIELD_HEIGHT * NUM_EDIT_FIELDS +
      (sortedTranslationRegionGroups[index].notes ? NOTES_FIELD_HEIGHT : EMPTY_NOTES_FIELD_HEIGHT) +
      // Most of the non-standard translation tables are long and end up wrapping, so add some extra padding.
      (sortedTranslationRegionGroups[index].codonTranslationTable === CodonTranslationTable.STANDARD
        ? 0
        : 30) +
      (CUSTOM_FIELD_BASE_HEIGHT +
        // Account for height of placeholder text when there are no custom fields
        CUSTOM_FIELD_ROW_HEIGHT * Math.max(sortedTranslationRegionGroups[index].customFields.length, 1)) +
      TRANSLATE_AS_METHIONINE_FIELD_HEIGHT +
      (REGION_BASE_HEIGHT + REGION_ROW_HEIGHT * sortedTranslationRegionGroups[index].translations.length) +
      (sortedTranslationRegionGroups[index].editable ? EDIT_FORM_BUTTONS_HEIGHT : 0) +
      28
    );
  }

  return (
    <div
      ref={panelRef}
      className={className}
      {...getTestProps({component: 'TranslationPanelContent', element: 'root'})}
    >
      {canEditSequenceFeatures && (
        <div className={css(styles.createButton)}>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.MINI}
            onClick={onClickCreate}
            text="Create new"
            dataTestAttrs={{
              component: 'TranslationPanelContent',
              element: 'createNewButton',
            }}
          />
        </div>
      )}
      <div className={css(styles.translationRowsSection)}>
        <VariableSizeList
          ref={listRef}
          itemData={{
            sortedTranslationRegionGroups,
            canEditSequenceFeatures,
            sequenceId,
            sequenceStartIndex,
            sequenceLength,
            sequenceBases,
            nucleotideType,
            isSingleStranded,
            isCircularSequence,
            onSelectTranslation,
            deleteTranslationRegionGroup,
            editTranslationRegionGroup,
            createNewTranslationRegionGroup,
            openExpandedTranslations,
            tagSchema,
          }}
          height={selectedTranslationIndex === -1 ? 200 : 590}
          width="100%"
          itemCount={sortedTranslationRegionGroups.length}
          overscanCount={20}
          itemSize={getTranslationPanelRowHeight}
          estimatedItemSize={BASE_FEATURE_ROW_HEIGHT}
          itemKey={(index) => `translationPanelRow-${sortedTranslationRegionGroups[index].cid}`}
        >
          {TranslationPanelRow}
        </VariableSizeList>
      </div>
      {translationPanelState.isCreateActive && canEditSequenceFeatures ? (
        <CreateEditTranslationForm
          allTranslationData={sortedTranslationRegionGroups}
          editedTranslationData={null}
          sequenceId={sequenceId}
          sequenceStartIndex={sequenceStartIndex}
          sequenceLength={sequenceLength}
          sequenceBases={sequenceBases}
          nucleotideType={nucleotideType}
          isSingleStranded={isSingleStranded}
          isCircularSequence={isCircularSequence}
          onClickCancel={() => {
            dispatch(cancelCreateNewTranslation(sequenceId));
          }}
          deleteTranslationRegionGroup={deleteTranslationRegionGroup}
          editTranslationRegionGroup={editTranslationRegionGroup}
          createNewTranslationRegionGroup={createNewTranslationRegionGroup}
          isReadOnly={false}
          tagSchema={tagSchema}
        />
      ) : (
        <Button
          className={css(styles.expandedViewButton)}
          buttonStyle={ButtonStyle.LINK}
          onClick={openExpandedTranslations}
          text="Show expanded view"
          dataTestAttrs={{component: 'TranslationPanelContent', element: 'expandedTranslationsLink'}}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  createButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '8px 12px',
    // Match sizing behavior of annotation panel so that the button is positioned the same.
    height: '44px',
    alignItems: 'center',
  },
  translationRowsSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  expandedViewButton: {
    width: '100%',
    padding: '8px 0',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
});
